import { useQuery } from "@tanstack/react-query";
import { errorMessage, useTRPC } from "../../lib/api";
import { ErrorText } from "../../ui";
import { Dialog } from "../../ui/overlay";

/**
 * "Show original" (architecture §6.4, WP-061): every header of a message as it arrived, and the
 * provider's other fields, from what sync kept (`mail.getRaw`). Shown as text, never rendered.
 */
export function RawDialog({ messageId, onClose }: { messageId: string; onClose: () => void }) {
  const trpc = useTRPC();
  const raw = useQuery(trpc.mail.getRaw.queryOptions({ messageId }));
  return (
    <Dialog title="Original message" onClose={onClose}>
      {raw.isPending ? <p className="text-sm text-muted">Loading…</p> : null}
      {raw.isError ? <ErrorText>The original could not be loaded: {errorMessage(raw.error)}</ErrorText> : null}
      {raw.data === undefined ? null : raw.data.capturedAt === null ? (
        <p className="text-sm text-muted">KitWork+ has not kept this message’s headers yet. Older mail gets them during its account’s next full read.</p>
      ) : (
        <div className="flex max-h-[70vh] flex-col gap-3 overflow-auto">
          {raw.data.truncated ? <p className="text-xs text-muted">Shortened to fit: the longest routing and signature headers were left out.</p> : null}
          <pre aria-label="Headers" className="whitespace-pre-wrap break-all font-mono text-xs text-text">
            {raw.data.headers.map((h) => `${h.name}: ${h.value}`).join("\n")}
          </pre>
          <details>
            <summary className="cursor-pointer text-sm text-muted">Provider fields</summary>
            <pre className="whitespace-pre-wrap break-all font-mono text-xs text-text">{JSON.stringify(raw.data.provider, null, 2)}</pre>
          </details>
        </div>
      )}
    </Dialog>
  );
}
